/**
 * @file Redux slice for managing report state and actions
 * @module reportSlice
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Report } from '../../types';
import {
	resolveAdditionalAvailableYears,
	withAdditionalAcademicYear,
} from './additionalDownloadsHelpers';

/**
 * Initial state for the report slice
 * @type {Report.State}
 */
const initialState: Report.State = {
	personalisedTargets: [],
	availableYears: [],
	selectedYear: undefined,
	myReports: [],
	reportData: {},
	additional: [],
	additionalData: {},
	loading: false,
	loadingMyReports: false,
	selectedYearForNewGroupReports: undefined,
	additionalAvailableYears: [],
	additionalSelectedYear: undefined,
};

/**
 * Redux slice containing reducers for report state management
 */
const report = createSlice({
	name: 'report',
	initialState,
	reducers: {
		/**
		 * Sets loading state when fetching personalised targets
		 * @param {Report.State} state Current state
		 */
		setPersonalisedTargetsPending(state: Report.State) {
			return {
				...state,
				loading: true,
			};
		},
		/**
		 * Updates state with successfully fetched personalised targets
		 * @param {Report.State} state Current state
		 * @param {PayloadAction<Array<Report.PersonalisedTargets>>} action Action containing personalised targets
		 */
		setPersonalisedTargetsSuccess(
			state: Report.State,
			action: PayloadAction<Array<Report.PersonalisedTargets>>
		) {
			const personalisedTargets: Array<Report.PersonalisedTargets> = action.payload;

			return {
				...state,
				personalisedTargets: personalisedTargets,
				loading: false,
			};
		},
		/**
		 * Handles failure when fetching personalised targets
		 * @param {Report.State} state Current state
		 */
		setPersonalisedTargetsFailed(state: Report.State) {
			return {
				...state,
				loading: false,
			};
		},
		/**
		 * Sets loading state when fetching my reports
		 * @param {Report.State} state Current state
		 */
		setMyReportsPending(state: Report.State) {
			return {
				...state,
				loadingMyReports: true,
			};
		},
		/**
		 * Handles failure when fetching my reports
		 * @param {Report.State} state Current state
		 */
		setMyReportsFailed(state: Report.State) {
			//error state?
			return {
				...state,
				loadingMyReports: false,
			};
		},
		/**
		 * Updates state with successfully fetched reports and available years
		 * @param {Report.State} state Current state
		 * @param {PayloadAction<{Reports: Report.Report[], AvailableYears: number[]}>} action Action containing reports and years
		 */
		setMyReportsSuccess(
			state: Report.State,
			action: PayloadAction<{ Reports: Report.Report[]; AvailableYears: number[] }>
		) {
			//custom report ordering
			const sortOrder = (name: string): number => {
				const split = name.split('/');

				const level = ['A', 'AS', 'BTEC', 'BTECCert', 'BTEC90Cred', 'KS4'].indexOf(split[0]);
				const validLevel = level > -1 ? level : 99;

				const report = [
					'Provider',
					'Disadvantaged',
					'Ethnicity',
					'Gender',
					'Governors',
					'HighGrades',
				].indexOf(split[1]);
				const validReport = report > -1 ? report : 9;

				return validLevel + validReport;
			};

			let reports = action.payload.Reports.map((report) => ({
				report,
				order: sortOrder(report.Name),
			})).sort((a, b) => a.order - b.order);
			let availableYears = action.payload.AvailableYears.sort((a, b) => b - a);
			let selectedYear =
				state.selectedYear && availableYears.find((x) => x == state.selectedYear)
					? state.selectedYear
					: availableYears[0];

			return {
				...state,
				loadingMyReports: false,
				myReports: reports.map((x) => x.report),
				availableYears: action.payload.AvailableYears,
				selectedYear: selectedYear,
			};
		},
		/**
		 * Sets loading state when fetching report data
		 * @param {Report.State} state Current state
		 */
		setReportDataPending(state: Report.State) {
			return {
				...state,
				loading: true,
			};
		},
		/**
		 * Handles failure when fetching report data
		 * @param {Report.State} state Current state
		 */
		setReportDataFailed(state: Report.State) {
			//error state?
			return {
				...state,
				loading: false,
			};
		},
		/**
		 * Updates state with successfully fetched report data
		 * @param {Report.State} state Current state
		 * @param {PayloadAction<{Report: Report.Report, PreviousReports: Report.Report[]}>} action Action containing report and previous reports
		 */
		setReportDataSuccess(
			state: Report.State,
			action: PayloadAction<{ Report: Report.Report; PreviousReports: Report.Report[] }>
		) {
			let reportData = {
				...state.reportData,
				[action.payload.Report.EntityKey]: {
					report: action.payload.Report,
					previousReports: action.payload.PreviousReports,
				},
			};

			return {
				...state,
				loading: false,
				reportData,
			};
		},
		/**
		 * Sets loading state when fetching additional data
		 * @param {Report.State} state Current state
		 */
		setAdditionalPending(state: Report.State) {
			return {
				...state,
				loading: true,
			};
		},
		/**
		 * Updates state with successfully fetched additional data
		 * @param {Report.State} state Current state
		 * @param {PayloadAction<{EnquiryJobs: Report.Additional[], AvailableYears: number[]}>} action Action containing enquiry jobs and years
		 */
		setAdditionalSuccess(
			state: Report.State,
			action: PayloadAction<{ EnquiryJobs: Report.Additional[]; AvailableYears: number[] }>
		) {
			const yearsFromApi = action.payload.AvailableYears ?? [];
			const additionalAvailableYears = resolveAdditionalAvailableYears(
				yearsFromApi,
				action.payload.EnquiryJobs
			);
			const fallbackYear = additionalAvailableYears[0];
			const additional = action.payload.EnquiryJobs.map((job) =>
				withAdditionalAcademicYear(job, fallbackYear)
			);
			const additionalSelectedYear =
				state.additionalSelectedYear &&
				additionalAvailableYears.find((year) => year === state.additionalSelectedYear)
					? state.additionalSelectedYear
					: additionalAvailableYears[0];

			return {
				...state,
				loading: false,
				additional,
				additionalAvailableYears,
				additionalSelectedYear,
			};
		},
		/**
		 * Handles failure when fetching additional data
		 * @param {Report.State} state Current state
		 */
		setAdditionalFailed(state: Report.State) {
			return {
				...state,
				loading: false,
			};
		},
		/**
		 * Sets loading state when fetching additional item data
		 * @param {Report.State} state Current state
		 */
		setAdditionalDataPending(state: Report.State) {
			return {
				...state,
				loading: true,
			};
		},
		/**
		 * Handles failure when fetching additional item data
		 * @param {Report.State} state Current state
		 */
		setAdditionalDataFailed(state: Report.State) {
			return {
				...state,
				loading: false,
			};
		},
		/**
		 * Updates state with successfully fetched additional item data
		 * @param {Report.State} state Current state
		 * @param {PayloadAction<Report.Additional & {Attachments: Report.Attachment[], Comments: Report.Comment[]}>} action Action containing attachments and comments
		 */
		setAdditionalDataSuccess(
			state: Report.State,
			action: PayloadAction<
				Report.Additional & { Attachments: Report.Attachment[]; Comments: Report.Comment[] }
			>
		) {
			return {
				...state,
				loading: false,
				additionalData: {
					...state.additionalData,
					[action.payload.Id]: {
						attachments: action.payload.Attachments,
						comments: action.payload.Comments,
					},
				},
			};
		},
		setSelectedYear(state: Report.State, action: PayloadAction<number>) {
			return {
				...state,
				selectedYear: action.payload,
			};
		},
		setSelectedYearForNewGroupReports(state: Report.State, action: PayloadAction<number>) {
			return {
				...state,
				selectedYearForNewGroupReports: action.payload,
			};
		},
		setAdditionalSelectedYear(state: Report.State, action: PayloadAction<number>) {
			return {
				...state,
				additionalSelectedYear: action.payload,
			};
		},
		setAvailableYears(state: Report.State, action: PayloadAction<any>) {
			return {
				...state,
				loading: false,
				availableYears: action.payload,
			};
		},
		setIsPending(state: Report.State) {
			state.loading = true;
		},
		setHasLoaded(state: Report.State) {
			state.loading = false;
		},
		clearReports(state: Report.State) {
			state.reportData = [];
		},
	},
});

/**
 ** Export Actions
 */
export const {
	setPersonalisedTargetsPending,
	setPersonalisedTargetsSuccess,
	setPersonalisedTargetsFailed,
	setMyReportsPending,
	setMyReportsSuccess,
	setMyReportsFailed,
	setReportDataPending,
	setReportDataSuccess,
	setReportDataFailed,
	setAdditionalPending,
	setAdditionalSuccess,
	setAdditionalFailed,
	setSelectedYear,
	setSelectedYearForNewGroupReports,
	setAdditionalSelectedYear,
	setAdditionalDataPending,
	setAdditionalDataFailed,
	setAdditionalDataSuccess,
	setAvailableYears,
	setIsPending,
	setHasLoaded,
	clearReports,
} = report.actions;

export default report.reducer;
